import { createRouter, createWebHistory } from 'vue-router';
import Home from '../views/Home.vue';
import Articles from '../views/Articles.vue';
import Article from '../components/Article.vue';
import Login from '../views/admin/Login.vue';
import ForgotPassword from '../views/admin/ForgotPassword.vue';
import ResetPassword from '../views/admin/ResetPassword.vue';
import Dashboard from '../views/admin/Dashboard.vue';
import ArticleForm from '../views/admin/ArticleForm.vue';
import Analytics from '../views/admin/Analytics.vue';
import Users from '../views/admin/Users.vue';
import UserForm from '../views/admin/UserForm.vue';
import NotFound from '../views/NotFound.vue';
import { authService } from '../utils/auth.js';
import { scrollPositionManager } from '../utils/scrollPosition.js';
import { setRobotsMeta } from '../utils/seo.js';

export const APP_TITLE = 'Brand Integrated';

/** @param {string | undefined} segment Page label before " | Brand Integrated"; omit for home. */
function formatDocumentTitle(segment) {
  if (!segment) return APP_TITLE;
  return `${segment} | ${APP_TITLE}`;
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
  },
  {
    path: '/articles',
    name: 'Articles',
    component: Articles,
    meta: { title: 'Articles' },
  },
  {
    path: '/insights',
    redirect: '/articles',
  },
  {
    path: '/article/:id',
    name: 'Article',
    component: Article,
    props: true,
  },
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: Login,
    meta: { requiresGuest: true, title: 'Admin login' },
  },
  {
    path: '/admin/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresGuest: true, title: 'Forgot password' },
  },
  {
    path: '/admin/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresGuest: true, title: 'Reset password' },
  },
  {
    path: '/admin/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true, title: 'Dashboard' },
  },
  {
    path: '/admin/articles/new',
    name: 'ArticleCreate',
    component: ArticleForm,
    meta: { requiresAuth: true, title: 'New article' },
  },
  {
    path: '/admin/articles/:id/edit',
    name: 'ArticleEdit',
    component: ArticleForm,
    meta: { requiresAuth: true, title: 'Edit article' },
  },
  {
    path: '/admin/analytics',
    name: 'Analytics',
    component: Analytics,
    meta: { requiresAuth: true, title: 'Analytics' },
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: Users,
    meta: { requiresAuth: true, title: 'Users' },
  },
  {
    path: '/admin/users/new',
    name: 'AdminUserCreate',
    component: UserForm,
    meta: { requiresAuth: true, title: 'New user' },
  },
  {
    path: '/admin/users/:id/edit',
    name: 'AdminUserEdit',
    component: UserForm,
    meta: { requiresAuth: true, title: 'Edit user' },
  },
  {
    path: '/admin',
    redirect: '/admin/dashboard',
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { title: 'Page not found' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from) {
    return new Promise((resolve) => {
      // If navigating to the same route, preserve current scroll position
      if (to.path === from.path) {
        resolve(false); // Don't scroll, maintain current position
        return;
      }
      
      // If navigating to a different route with hash, scroll to the hash
      if (to.hash) {
        resolve({
          el: to.hash,
          behavior: 'smooth',
        });
        return;
      }
      
      // For all other navigation, always scroll to top
      resolve({ top: 0, behavior: 'smooth' });
    });
  },
});

// Navigation guards
router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest);
  const isAuthenticated = authService.isAuthenticated();

  if (requiresAuth && !isAuthenticated) {
    // Redirect to login if trying to access protected route
    next('/admin/login');
  } else if (requiresGuest && isAuthenticated) {
    // Redirect to dashboard if already logged in
    next('/admin/dashboard');
  } else if (to.name === 'AdminUserEdit') {
    const currentUserId = authService.getCurrentUser()?.userId;
    if (to.params.id !== currentUserId) {
      sessionStorage.setItem(
        'adminFlash',
        JSON.stringify({ message: 'You can only edit your own profile', type: 'error' })
      );
      next('/admin/users');
    } else {
      next();
    }
  } else {
    next();
  }
});

router.afterEach((to) => {
  if (to.path.startsWith('/admin')) {
    setRobotsMeta('noindex, nofollow');
    document.title = formatDocumentTitle(to.meta?.title);
    return;
  }

  if (to.name === 'Article') return;

  setRobotsMeta('index, follow');
  document.title = formatDocumentTitle(to.meta?.title);
});

export default router;

// Initialize scroll position manager
scrollPositionManager.init(router);
